import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
    fetchMovementSheet,
    resetMovementSheet,
    selectMovementSheetData,
    selectMovementSheetStatus,
    selectMovementSheetError,
} from 'redux/slices/sla-reports/movement-sheet';
import ReportHeader from './report-header';
import { sanitizeSpreadsheetCell, sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';

const SHEET_COLUMNS = ['WCHR', 'WCHS', 'WCHC', 'MAAS', 'BLIND', 'DEAF', 'OTHER', 'TOTAL'];
const SECTIONS = [
    { key: 'preNotified', label: 'Pre-Notified' },
    { key: 'nonNotified', label: 'Non-Notified' },
    { key: 'actuals', label: 'Actuals' },
];

const EMPTY_BUCKETS = () => ({
    WCHR: 0,
    WCHS: 0,
    WCHC: 0,
    MAAS: 0,
    BLIND: 0,
    DEAF: 0,
    OTHER: 0,
    total: 0,
});

const sumBuckets = (a, b) => {
    const result = EMPTY_BUCKETS();
    SHEET_COLUMNS.forEach((col) => {
        const key = col === 'TOTAL' ? 'total' : col;
        result[key] = (a?.[key] || 0) + (b?.[key] || 0);
    });
    return result;
};

const getBucketsForSection = (entry, sectionKey) => entry?.[sectionKey] || EMPTY_BUCKETS();

const formatPeriodLabel = (startDate, endDate) => {
    if (!startDate) return '';
    const start = moment(startDate).format('Do MMMM');
    if (!endDate || startDate === endDate) {
        return start;
    }
    const end = moment(endDate).format('Do MMMM');
    return `${start} - ${end}`;
};

const buildSheetRows = (movementSheet, movementSheetAirlines) => {
    const days = movementSheet || [];
    const airlines = movementSheetAirlines || [];
    const sections = [];
    const grandTotals = {
        preNotified: EMPTY_BUCKETS(),
        nonNotified: EMPTY_BUCKETS(),
        actuals: EMPTY_BUCKETS(),
    };

    airlines.forEach(({ code, airline }) => {
        const airlineSection = { code, airline, dayRows: [], totals: null };

        const totals = {
            preNotified: EMPTY_BUCKETS(),
            nonNotified: EMPTY_BUCKETS(),
            actuals: EMPTY_BUCKETS(),
        };

        days.forEach((day, index) => {
            const entry = day.airlines?.find((a) => a.code === code);
            const preNotified = getBucketsForSection(entry, 'preNotified');
            const nonNotified = getBucketsForSection(entry, 'nonNotified');
            const actuals = getBucketsForSection(entry, 'actuals');

            totals.preNotified = sumBuckets(totals.preNotified, preNotified);
            totals.nonNotified = sumBuckets(totals.nonNotified, nonNotified);
            totals.actuals = sumBuckets(totals.actuals, actuals);

            airlineSection.dayRows.push({
                rowNum: index + 1,
                date: day.date,
                preNotified,
                nonNotified,
                actuals,
            });
        });

        airlineSection.totals = totals;
        grandTotals.preNotified = sumBuckets(grandTotals.preNotified, totals.preNotified);
        grandTotals.nonNotified = sumBuckets(grandTotals.nonNotified, totals.nonNotified);
        grandTotals.actuals = sumBuckets(grandTotals.actuals, totals.actuals);
        sections.push(airlineSection);
    });

    return { sections, grandTotals };
};

const bucketCells = (buckets) =>
    SHEET_COLUMNS.map((col) => {
        const key = col === 'TOTAL' ? 'total' : col;
        return buckets?.[key] ?? 0;
    });

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 6px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '12px',
    color: '#374151',
    whiteSpace: 'nowrap',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '6px 8px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#1f2937',
};

const labelCellStyle = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
};

const totalsRowStyle = {
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
};

const exportButtonStyle = {
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
};

const SectionHeaderRows = ({ periodLabel }) => (
    <>
        <tr>
            <th style={headerStyle}>Date</th>
            <th style={headerStyle}>Airline</th>
            {SECTIONS.map(({ label }) => (
                <th key={label} colSpan={SHEET_COLUMNS.length} style={headerStyle}>
                    {label}
                </th>
            ))}
        </tr>
        <tr>
            <th style={headerStyle}>{periodLabel}</th>
            <th style={headerStyle} />
            {SECTIONS.map(({ key }) =>
                SHEET_COLUMNS.map((col) => (
                    <th key={`${key}-${col}`} style={headerStyle}>{col}</th>
                ))
            )}
        </tr>
    </>
);

const DataRow = ({ rowNum, airline, preNotified, nonNotified, actuals, rowStyle }) => (
    <tr style={rowStyle}>
        <td style={cellStyle}>{rowNum}</td>
        <td style={labelCellStyle}>{airline}</td>
        {bucketCells(preNotified).map((val, i) => (
            <td key={`pre-${i}`} style={cellStyle}>{val || ''}</td>
        ))}
        {bucketCells(nonNotified).map((val, i) => (
            <td key={`non-${i}`} style={cellStyle}>{val || ''}</td>
        ))}
        {bucketCells(actuals).map((val, i) => (
            <td key={`act-${i}`} style={cellStyle}>{val || ''}</td>
        ))}
    </tr>
);

const exportToCsv = (periodLabel, sections, grandTotals) => {
    const esc = (v) => `"${String(sanitizeSpreadsheetCell(v ?? '')).replace(/"/g, '""')}"`;
    const lines = [];

    const header1 = ['Date', 'Airline', ...SECTIONS.flatMap(({ label }) => [label, ...Array(SHEET_COLUMNS.length - 1).fill('')])];
    const header2 = [periodLabel, '', ...SECTIONS.flatMap(() => SHEET_COLUMNS)];
    lines.push(header1.map(esc).join('\t'));
    lines.push(header2.map(esc).join('\t'));

    sections.forEach(({ airline, dayRows, totals }) => {
        dayRows.forEach(({ rowNum, preNotified, nonNotified, actuals }) => {
            lines.push([
                rowNum,
                airline,
                ...bucketCells(preNotified),
                ...bucketCells(nonNotified),
                ...bucketCells(actuals),
            ].map(esc).join('\t'));
        });
        lines.push([
            '',
            'Totals',
            ...bucketCells(totals.preNotified),
            ...bucketCells(totals.nonNotified),
            ...bucketCells(totals.actuals),
        ].map(esc).join('\t'));
        lines.push('');
    });

    lines.push([
        '',
        'Grand Total',
        ...bucketCells(grandTotals.preNotified),
        ...bucketCells(grandTotals.nonNotified),
        ...bucketCells(grandTotals.actuals),
    ].map(esc).join('\t'));

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `movement-sheet-${(periodLabel || 'report').replace(/\s+/g, '-').toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const exportToExcel = async (periodLabel, sections, grandTotals) => {
    const Excel = await import('exceljs/dist/exceljs.min.js');
    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet('Movement Sheet');

    const COLORS = { headerBg: 'FFF9FAFB', headerText: 'FF374151', totalBg: 'FFF3F4F6' };
    const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
        cell.border = border;
        cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
        if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
        if (font) cell.font = font;
    };

    sheet.getColumn(1).width = 8;
    sheet.getColumn(2).width = 18;
    for (let c = 3; c <= 2 + SECTIONS.length * SHEET_COLUMNS.length; c++) {
        sheet.getColumn(c).width = 8;
    }

    const row1 = sheet.addRow([
        'Date',
        'Airline',
        ...SECTIONS.flatMap(({ label }) => [label, ...Array(SHEET_COLUMNS.length - 1).fill('')]),
    ]);
    row1.eachCell((cell, colNumber) => {
        applyCell(cell, { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 11 }, align: colNumber <= 2 ? 'left' : 'center' });
    });

    SECTIONS.forEach((_, sectionIndex) => {
        const startCol = 3 + sectionIndex * SHEET_COLUMNS.length;
        sheet.mergeCells(1, startCol, 1, startCol + SHEET_COLUMNS.length - 1);
    });

    const row2 = sheet.addRow([periodLabel, '', ...SECTIONS.flatMap(() => SHEET_COLUMNS)]);
    row2.eachCell((cell, colNumber) => {
        if (colNumber > 2) {
            applyCell(cell, { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 10 } });
        }
    });

    sections.forEach(({ airline, dayRows, totals }) => {
        dayRows.forEach(({ rowNum, preNotified, nonNotified, actuals }) => {
            const row = sheet.addRow([
                rowNum,
                airline,
                ...bucketCells(preNotified),
                ...bucketCells(nonNotified),
                ...bucketCells(actuals),
            ]);
            applyCell(row.getCell(1));
            applyCell(row.getCell(2), { align: 'left' });
            for (let c = 3; c <= row.cellCount; c++) applyCell(row.getCell(c));
        });

        const totalsRow = sheet.addRow([
            '',
            'Totals',
            ...bucketCells(totals.preNotified),
            ...bucketCells(totals.nonNotified),
            ...bucketCells(totals.actuals),
        ]);
        totalsRow.eachCell((cell, colNumber) => {
            applyCell(cell, {
                fill: COLORS.totalBg,
                font: { bold: true },
                align: colNumber === 2 ? 'left' : 'center',
            });
        });
    });

    const grandRow = sheet.addRow([
        '',
        'Grand Total',
        ...bucketCells(grandTotals.preNotified),
        ...bucketCells(grandTotals.nonNotified),
        ...bucketCells(grandTotals.actuals),
    ]);
    grandRow.eachCell((cell, colNumber) => {
        applyCell(cell, {
            fill: COLORS.totalBg,
            font: { bold: true },
            align: colNumber === 2 ? 'left' : 'center',
        });
    });

    sanitizeExcelWorkbook(workbook);
    const buf = await workbook.xlsx.writeBuffer();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    link.download = `movement-sheet-${(periodLabel || 'report').replace(/\s+/g, '-').toLowerCase()}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const MovementSheetReport = ({ dateRange, iata, runReportRef, onRunLoadingChange }) => {
    const dispatch = useDispatch();
    const data = useSelector(selectMovementSheetData);
    const status = useSelector(selectMovementSheetStatus);
    const error = useSelector(selectMovementSheetError);
    const isLoading = status === 'loading';

    const handleRunReport = useCallback(() => {
        const start = dateRange?.[0];
        const end = dateRange?.[1];
        if (!start || !end || !iata) return;

        dispatch(fetchMovementSheet({
            startDate: moment(new Date(start)).format().split('T')[0],
            endDate: moment(new Date(end)).format().split('T')[0],
            iata,
            groupByAirline: true,
        }));
    }, [dispatch, dateRange, iata]);

    useEffect(() => {
        return () => {
            dispatch(resetMovementSheet());
        };
    }, [dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    const periodLabel = useMemo(
        () => formatPeriodLabel(data?.startDate, data?.endDate),
        [data?.startDate, data?.endDate]
    );

    const { sections, grandTotals } = useMemo(
        () => buildSheetRows(data?.movementSheet, data?.movementSheetAirlines),
        [data?.movementSheet, data?.movementSheetAirlines]
    );

    const hasData = sections.length > 0;

    return (
        <div>
            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
            />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>
                    Error loading Movement Sheet: {error}
                </p>
            )}

            {data && !hasData && status === 'succeeded' && (
                <p style={{ fontSize: '13px', color: '#6b7280' }}>No movement sheet data available for this date range.</p>
            )}

            {hasData && (
                <div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={() => exportToCsv(periodLabel, sections, grandTotals)}
                            style={{ ...exportButtonStyle, backgroundColor: '#f3f4f6', color: '#374151' }}
                        >
                            Export CSV
                        </button>
                        <button
                            type="button"
                            onClick={() => exportToExcel(periodLabel, sections, grandTotals)}
                            style={{ ...exportButtonStyle, backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}
                        >
                            Export Excel
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
                        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                <SectionHeaderRows periodLabel={periodLabel} />
                            </thead>
                            <tbody>
                                {sections.map(({ code, airline, dayRows, totals }) => (
                                    <React.Fragment key={code}>
                                        {dayRows.map(({ rowNum, preNotified, nonNotified, actuals }) => (
                                            <DataRow
                                                key={`${code}-${rowNum}`}
                                                rowNum={rowNum}
                                                airline={airline}
                                                preNotified={preNotified}
                                                nonNotified={nonNotified}
                                                actuals={actuals}
                                            />
                                        ))}
                                        <DataRow
                                            rowNum=""
                                            airline="Totals"
                                            preNotified={totals.preNotified}
                                            nonNotified={totals.nonNotified}
                                            actuals={totals.actuals}
                                            rowStyle={totalsRowStyle}
                                        />
                                        <tr><td colSpan={2 + SECTIONS.length * SHEET_COLUMNS.length} style={{ height: '8px', border: 'none' }} /></tr>
                                    </React.Fragment>
                                ))}
                                <DataRow
                                    rowNum=""
                                    airline="Grand Total"
                                    preNotified={grandTotals.preNotified}
                                    nonNotified={grandTotals.nonNotified}
                                    actuals={grandTotals.actuals}
                                    rowStyle={{ ...totalsRowStyle, backgroundColor: '#e5e7eb' }}
                                />
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MovementSheetReport;
